import { useUser } from "@clerk/clerk-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FaBullhorn,
    FaChevronDown,
    FaEye,
    FaMapMarkerAlt,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import AISummary from "../../components/AISummary/AISummary";
import BumpYourNetwork from "../../components/BumpYourNetwork/BumpYourNetwork";
import MapView from "../../components/MapView/MapView";
import QuoteModal from "../../components/QuoteModal/QuoteModal";
import RecommendationCard from "../../components/RecommendationCard/RecommendationCard";
import ReviewModal from "../../components/ReviewModal/ReviewModal";
import SuccessModal from "../../components/SuccessModal/SuccessModal";
import { API_URL } from "../../utils/constants";
import { getMentionedUsernames } from "../../utils/mentionUtils";
import "./Search.css";

// US States list for location selector
const US_STATES = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
];

const Search = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isSignedIn, isLoaded: isClerkLoaded } = useUser();

    const params = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );
    const query = useMemo(() => params.get("q"), [params]);
    const stateParam = useMemo(() => params.get("state"), [params]);
    const noResultsParam = useMemo(
        () => params.get("noResults") === "true",
        [params]
    );

    const initialProvidersRef = useRef(location.state?.initialProviders);
    const processedInitialProvidersRef = useRef(false);

    const [rawProviders, setRawProviders] = useState([]);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [currentUserEmail, setCurrentUserEmail] = useState(null);
    const [currentUserProfileImage, setCurrentUserProfileImage] = useState(null);
    const [currentUserPreferredName, setCurrentUserPreferredName] = useState(null);

    const [error, setError] = useState(null);
    const [searchTimeout, setSearchTimeout] = useState(null); // Track search timeout
    const [searchStartTime, setSearchStartTime] = useState(null); // Track when search started

    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
    const [clickedRecommender, setClickedRecommender] = useState(null);
    const [showFeatureComingModal, setShowFeatureComingModal] = useState(false);
    const [showBumpNetworkModal, setShowBumpNetworkModal] = useState(false);
    const [likedRecommendations, setLikedRecommendations] = useState(new Set());
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [selectedCities, setSelectedCities] = useState([]);
    const [showCityFilter, setShowCityFilter] = useState(false);
    const [showAISummary, setShowAISummary] = useState(true);

    // Batch comments state
    const [commentsMap, setCommentsMap] = useState(new Map());
    const [isLoadingComments, setIsLoadingComments] = useState(false);

    const [isLoading, setIsLoading] = useState(!!query);

    // New state for map view
    const [viewMode, setViewMode] = useState("list"); // 'list' or 'map'
    const [userLocation, setUserLocation] = useState(null);

    // Apply city filtering to providers - defined early so it's available throughout
    const filteredProviders = useMemo(() => {
        if (!rawProviders || rawProviders.length === 0) return [];

        let list = [...rawProviders];

        // Apply city filter only - preserve backend ordering
        if (selectedCities.length > 0) {
            list = list.filter((p) => {
                const city = p.city || "Other";
                return selectedCities.includes(city);
            });
        }

        // Return filtered list without re-sorting to preserve backend order
        return list;
    }, [rawProviders, selectedCities]);

    useEffect(() => {
        if (isClerkLoaded) {
            if (isSignedIn && user) {
                setCurrentUserId(user.id);
                setCurrentUserEmail(user.primaryEmailAddress?.emailAddress);
                setCurrentUserProfileImage(user.imageUrl);
                setCurrentUserPreferredName(user.firstName || user.username);
                
                // Fetch user's profile image from our backend
                fetchUserProfileImage(user.id, user.primaryEmailAddress?.emailAddress);
            } else {
                setCurrentUserId(null);
                setCurrentUserEmail(null);
                setCurrentUserProfileImage(null);
                setCurrentUserPreferredName(null);
            }
        }
    }, [user, isSignedIn, isClerkLoaded]);

    const fetchUserProfileImage = async (userId, email) => {
        if (!userId || !email) return;
        
        try {
            const params = new URLSearchParams({
                user_id: userId,
                email: email,
                firstName: user?.firstName || "",
                lastName: user?.lastName || "",
                phoneNumber: user?.primaryPhoneNumber?.phoneNumber || "",
            });
            
            const response = await fetch(`${API_URL}/api/users/me/basic-info?${params.toString()}`);
            if (response.ok) {
                const data = await response.json();
                console.log('Backend profile data response:', data);
                if (data.success && data.profileImage) {
                    console.log('Setting profile image from backend:', data.profileImage);
                    setCurrentUserProfileImage(data.profileImage);
                } else {
                    console.log('No profile image from backend, keeping Clerk image');
                }
                if (data.success && data.preferredName) {
                    setCurrentUserPreferredName(data.preferredName);
                } else if (data.success && data.userName) {
                    setCurrentUserPreferredName(data.userName);
                }
            } else {
                console.log('Backend API response not ok:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('Error fetching user profile image:', error);
            // Keep the Clerk imageUrl as fallback
        }
    };

    useEffect(() => {
        if (
            location.state?.initialProviders &&
            initialProvidersRef.current !== location.state.initialProviders
        ) {
            initialProvidersRef.current = location.state.initialProviders;
            processedInitialProvidersRef.current = false;
        }
    }, [location.state?.initialProviders]);

    const processAndSetProviders = async (providersToProcess) => {
        console.log(`[SEARCH] 🔄 Processing ${providersToProcess?.length || 0} providers:`, {
            isArray: Array.isArray(providersToProcess),
            firstProvider: providersToProcess?.[0] ? {
                id: providersToProcess[0].id,
                business_name: providersToProcess[0].business_name,
                city: providersToProcess[0].city,
                state: providersToProcess[0].state
            } : null
        });
        
        if (!Array.isArray(providersToProcess)) {
            console.log(`[SEARCH] ⚠️ providersToProcess is not an array, setting empty results`);
            setRawProviders([]);
            setLikedRecommendations(new Set());
            return;
        }

        // Use review data directly from service_providers table instead of individual API calls
        const newInitialUserLikes = new Set();
        const enriched = providersToProcess
            .map((p, idx) => {
                if (!p || typeof p.id === "undefined") {
                    console.log(`[SEARCH] ⚠️ Skipping invalid provider at index ${idx}:`, p);
                    return null;
                }
                if (p.currentUserLiked) {
                    newInitialUserLikes.add(p.id);
                }
                return {
                    ...p,
                    originalIndex: idx,
                    average_rating: parseFloat(p.average_rating) || 0,
                    total_reviews: parseInt(p.total_reviews, 10) || 0,
                    users_who_reviewed: p.users_who_reviewed || [],
                };
            })
            .filter((p) => p !== null);

        console.log(`[SEARCH] ✅ Processed ${enriched.length} providers, setting rawProviders`);
        
        setLikedRecommendations(newInitialUserLikes);
        setRawProviders(enriched);
    };

    useEffect(() => {
        const passedProviders = initialProvidersRef.current;

        if (passedProviders && !processedInitialProvidersRef.current && query) {
            setIsLoading(true);
            processAndSetProviders(passedProviders).finally(() => {
                setIsLoading(false);
                processedInitialProvidersRef.current = true;
            });
            return;
        }

        if (!query) {
            setRawProviders([]);
            setLikedRecommendations(new Set());
            setIsLoading(false);
            initialProvidersRef.current = null;
            processedInitialProvidersRef.current = false;
            return;
        }
        
        // Don't search until we have all required data
        if (!currentUserId || !currentUserEmail) {
            console.log(`[SEARCH] Waiting for user data: userId=${!!currentUserId}, email=${!!currentUserEmail}`);
            return;
        }
        
        // Check if this is a spatial query
        const isSpatialQuery = query.toLowerCase().includes('near me') || 
                              query.toLowerCase().includes('close to me') || 
                              query.toLowerCase().includes('around me');

        if (!isClerkLoaded) {
            setIsLoading(true);
            return;
        }

        if (!currentUserId || !currentUserEmail) {
            if (query && isSignedIn) {
                setError(
                    "User details are still loading. Please wait briefly."
                );
            } else if (query && !isSignedIn) {
                setError("Please log in to perform a search.");
            }
            setIsLoading(false);
            setRawProviders([]);
            setLikedRecommendations(new Set());
            return;
        }

        // Prevent duplicate searches
        if (initialProvidersRef.current !== null) {
            console.log(`[SEARCH] Search already in progress, skipping duplicate`);
            return;
        }
        
        console.log(`[SEARCH] 🚀 Starting search for: "${query}"`);
        console.log(`[SEARCH] 📍 Is spatial query: ${isSpatialQuery ? 'Yes' : 'No'}`);
        
        setIsLoading(true);
        setError(null); // Clear any previous errors
        initialProvidersRef.current = null;
        processedInitialProvidersRef.current = false;

        (async () => {
            try {
                // Mark search as in progress
                initialProvidersRef.current = 'searching';
                setSearchStartTime(Date.now());
                
                // Set a reasonable timeout for search (e.g., 45 seconds)
                const timeoutId = setTimeout(() => {
                    console.log(`[SEARCH] ⏰ Search timeout reached (45s) - search may still be in progress on backend`);
                    // Don't set error here - just log that timeout was reached
                    // The search might still complete successfully
                }, 45000);
                setSearchTimeout(timeoutId);
                
                // For spatial queries, get location first, then execute search
                let finalUserLocation = userLocation;
                if (isSpatialQuery && (!userLocation || !userLocation.hasCoordinates)) {
                    console.log(`[SEARCH] 🔄 Getting location for spatial query...`);
                    finalUserLocation = await getLocationPromise();
                    console.log(`[SEARCH] ✅ Location acquired: ${finalUserLocation.lat}, ${finalUserLocation.lng}`);
                    
                    // Update the location state for future use
                    setUserLocation(finalUserLocation);
                }
                
                console.log(`[SEARCH] Executing search with location: ${finalUserLocation ? `${finalUserLocation.lat}, ${finalUserLocation.lng}` : 'None'}`);
                
                // Extract mentioned usernames from the query
                const mentionedUsernames = getMentionedUsernames(query);
                
                // Get user location for spatial search
                let locationParams = '';
                if (finalUserLocation && finalUserLocation.hasCoordinates) {
                    locationParams = `&latitude=${finalUserLocation.lat}&longitude=${finalUserLocation.lng}&max_distance=50000`;
                    console.log(`[SEARCH] ✅ Including location in search: ${finalUserLocation.lat}, ${finalUserLocation.lng}`);
                } else {
                    console.log(`[SEARCH] ⚠️ No location available for search - will use state-based fallback`);
                }
                
                console.log(`[SEARCH] 🎯 Executing search with final location:`, {
                    hasLocation: !!finalUserLocation,
                    hasCoordinates: finalUserLocation?.hasCoordinates,
                    lat: finalUserLocation?.lat,
                    lng: finalUserLocation?.lng,
                    locationParams
                });
                
                let fetchUrl = `${API_URL}/api/providers/search?q=${encodeURIComponent(
                    query
                )}&user_id=${currentUserId}&email=${encodeURIComponent(
                    currentUserEmail
                )}${locationParams}`;
                if (stateParam) {
                    fetchUrl += `&state=${encodeURIComponent(stateParam)}`;
                }
                // Add mentioned users as a parameter if any are found
                if (mentionedUsernames.length > 0) {
                    fetchUrl += `&mentioned_users=${encodeURIComponent(mentionedUsernames.join(','))}`;
                }
                
                console.log(`[SEARCH] 🔍 Final search URL: ${fetchUrl}`);
                console.log(`[SEARCH] 📍 Location params included: ${locationParams ? 'Yes' : 'No'}`);
                
                const res = await fetch(fetchUrl);
                if (!res.ok) {
                    const errorData = await res
                        .json()
                        .catch(() => ({
                            message: `Search error: ${res.status}`,
                        }));
                    throw new Error(
                        errorData.message ||
                            errorData.error ||
                            `Search error: ${res.status}`
                    );
                }
                const data = await res.json();
                if (!data.success) {
                    throw new Error(
                        data.message || data.error || "Search not successful"
                    );
                }
                
                console.log(`[SEARCH] 📥 Received response:`, {
                    success: data.success,
                    providersCount: data.providers?.length || 0,
                    totalResults: data.totalResults,
                    firstProvider: data.providers?.[0] ? {
                        id: data.providers[0].id,
                        business_name: data.providers[0].business_name,
                        city: data.providers[0].city,
                        state: data.providers[0].state
                    } : null
                });
                
                await processAndSetProviders(data.providers || []);
                
                console.log(`[SEARCH] ✅ Search completed successfully with ${data.providers?.length || 0} results`);
                
                // Mark search as completed
                initialProvidersRef.current = 'completed';

                // Construct the full, correct URL that should be in the address bar
                const targetUrl = new URL(window.location.href);
                targetUrl.searchParams.set("q", query);
                targetUrl.searchParams.set("user_id", currentUserId);
                targetUrl.searchParams.set("email", currentUserEmail);
                if (stateParam) {
                    targetUrl.searchParams.set("state", stateParam);
                } else {
                    targetUrl.searchParams.delete("state");
                }

                // Only navigate if the constructed URL is different from the current one
                if (targetUrl.href !== window.location.href) {
                    navigate(targetUrl.pathname + targetUrl.search, {
                        replace: true,
                        state: {
                            prevQuery: query,
                            currentSearchUserId: currentUserId,
                        },
                    });
                }
            } catch (err) {
                console.log(`[SEARCH] ❌ Search failed: ${err.message}`);
                setError(err.message || "Failed to fetch search results.");
                setRawProviders([]);
                setLikedRecommendations(new Set());
                // Mark search as completed (with error)
                initialProvidersRef.current = 'error';
            } finally {
                setIsLoading(false);
                clearTimeout(searchTimeout); // Clear the timeout on completion or error
            }
        })();
    }, [
        query,
        stateParam,
        currentUserId,
        currentUserEmail,
        navigate,
        location.search,
        isClerkLoaded,
        isSignedIn,
        userLocation, // Add userLocation to dependencies so search re-runs when location is available
        searchTimeout, // Add searchTimeout to dependencies
    ]);

    // Check if search is still in progress (even if loading state is false)
    const isSearchInProgress = useMemo(() => {
        // If we have a search start time and it's been less than 60 seconds, consider search still in progress
        if (searchStartTime && (Date.now() - searchStartTime) < 60000) {
            return true;
        }
        // If we're explicitly loading, search is in progress
        if (isLoading) {
            return true;
        }
        // If we have initial providers marked as 'searching', search is in progress
        if (initialProvidersRef.current === 'searching') {
            return true;
        }
        // If search is marked as completed or error, it's not in progress
        if (initialProvidersRef.current === 'completed' || initialProvidersRef.current === 'error') {
            return false;
        }
        return false;
    }, [searchStartTime, isLoading]);

    // Debug logging for search state
    useEffect(() => {
        if (query) {
            console.log(`[SEARCH DEBUG] Search state:`, {
                query,
                isLoading,
                isSearchInProgress,
                searchStartTime: searchStartTime ? new Date(searchStartTime).toISOString() : null,
                searchDuration: searchStartTime ? Math.floor((Date.now() - searchStartTime) / 1000) : 0,
                initialProvidersRef: initialProvidersRef.current,
                rawProvidersLength: rawProviders.length,
                filteredProvidersLength: filteredProviders.length,
                selectedCities: selectedCities,
                willShowBumpNetwork: (noResultsParam || filteredProviders.length === 0) && query && !isLoading && !isSearchInProgress
            });
        }
    }, [query, isLoading, isSearchInProgress, searchStartTime, rawProviders.length, filteredProviders.length, selectedCities, noResultsParam]);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    // Reset search state when query changes
    useEffect(() => {
        if (query) {
            initialProvidersRef.current = null;
            processedInitialProvidersRef.current = false;
            setSearchStartTime(null);
            setSearchTimeout(null);
            console.log(`[SEARCH] Query changed to: "${query}", resetting search state`);
        }
    }, [query]);
    
    // Fetch comments when providers are loaded
    useEffect(() => {
        if (!isLoading && rawProviders.length > 0) {
            fetchBatchComments(rawProviders);
        }
    }, [rawProviders, isLoading]);

    // Promise-based location service with caching
    const locationCache = useRef(null);
    
    const getLocationPromise = useCallback(() => {
        // Return cached location if available and not expired
        if (locationCache.current && locationCache.current.timestamp > Date.now() - 300000) { // 5 minutes
            console.log(`[SEARCH] Using cached location: ${locationCache.current.location.lat}, ${locationCache.current.location.lng}`);
            return Promise.resolve(locationCache.current.location);
        }
        
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                console.log("[SEARCH] Requesting user location for spatial search...");
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const newLocation = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                            hasCoordinates: true,
                            type: 'coordinates',
                            accuracy: 'high',
                            source: 'user_provided'
                        };
                        console.log(`[SEARCH] Location obtained: ${newLocation.lat}, ${newLocation.lng}`);
                        
                        // Cache the location
                        locationCache.current = {
                            location: newLocation,
                            timestamp: Date.now()
                        };
                        
                        resolve(newLocation);
                    },
                    (error) => {
                        console.log("Geolocation error:", error);
                        // Fallback to a default location (USA center)
                        const fallbackLocation = {
                            lat: 39.8283,
                            lng: -98.5795,
                            hasCoordinates: true,
                            type: 'coordinates',
                            accuracy: 'low',
                            source: 'fallback'
                        };
                        console.log(`[SEARCH] Using fallback location: ${fallbackLocation.lat}, ${fallbackLocation.lng}`);
                        
                        // Cache the fallback location
                        locationCache.current = {
                            location: fallbackLocation,
                            timestamp: Date.now()
                        };
                        
                        resolve(fallbackLocation);
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 300000, // 5 minutes
                    }
                );
            } else {
                // Fallback for browsers that don't support geolocation
                const fallbackLocation = {
                    lat: 39.8283,
                    lng: -98.5795,
                    hasCoordinates: true,
                    type: 'coordinates',
                    accuracy: 'low',
                    source: 'fallback'
                };
                console.log(`[SEARCH] Browser doesn't support geolocation, using fallback: ${fallbackLocation.lat}, ${fallbackLocation.lng}`);
                
                // Cache the fallback location
                locationCache.current = {
                    location: fallbackLocation,
                    timestamp: Date.now()
                };
                
                resolve(fallbackLocation);
            }
        });
    }, []);

    const handleCitySelection = (cityName) => {
        setSelectedCities((prev) =>
            prev.includes(cityName)
                ? prev.filter((c) => c !== cityName)
                : [...prev, cityName]
        );
    };

    const availableCities = useMemo(() => {
        if (!rawProviders || rawProviders.length === 0) return [];
        const cityCounts = rawProviders.reduce((acc, rec) => {
            const city = rec.city || "Other";
            acc[city] = (acc[city] || 0) + 1;
            return acc;
        }, {});

        return Object.entries(cityCounts).sort(
            ([, countA], [, countB]) => countB - countA
        );
    }, [rawProviders]);

    const handleReviewSubmit = async ({ rating, review, tags }) => {
        if (!currentUserEmail) {
            alert("User email not found. Cannot submit review.");
            return;
        }
        if (!currentUserId) {
            alert("User ID not found. Cannot submit review.");
            return;
        }
        if (!selectedProvider) {
            alert("Provider details missing. Cannot submit review.");
            return;
        }
        try {
            const response = await fetch(`${API_URL}/api/reviews`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    provider_id: selectedProvider.id,
                    provider_email: selectedProvider.email || "",
                    user_id: currentUserId,
                    email: currentUserEmail,
                    rating,
                    content: review,
                    tags,
                }),
            });
            if (!response.ok) {
                const errText = await response.text();
                throw new Error(errText || "Failed to submit review");
            }

            // Show success modal
            setSuccessMessage(
                `Thank you for reviewing ${selectedProvider.business_name}! Your feedback helps others make better decisions.`
            );
            setIsSuccessModalOpen(true);

            // Update the specific provider with new review data
            try {
                const params = new URLSearchParams({
                    user_id: currentUserId,
                    email: currentUserEmail,
                });
                const providerResponse = await fetch(
                    `${API_URL}/api/providers/${
                        selectedProvider.id
                    }?${params.toString()}`
                );

                if (providerResponse.ok) {
                    const data = await providerResponse.json();
                    if (data.success && data.provider) {
                        const updatedProvider = data.provider;
                        setRawProviders((prevProviders) => {
                            return prevProviders.map((provider) => {
                                if (provider.id === selectedProvider.id) {
                                    const existingTags = Array.isArray(
                                        provider.tags
                                    )
                                        ? provider.tags
                                        : [];
                                    const newTags = Array.isArray(tags)
                                        ? tags
                                        : [];
                                    const allTags = [
                                        ...new Set([
                                            ...existingTags,
                                            ...newTags,
                                        ]),
                                    ];

                                    return {
                                        ...provider,
                                        average_rating:
                                            parseFloat(
                                                updatedProvider.average_rating
                                            ) || provider.average_rating,
                                        total_reviews:
                                            parseInt(
                                                updatedProvider.total_reviews,
                                                10
                                            ) || provider.total_reviews,
                                        users_who_reviewed:
                                            updatedProvider.users_who_reviewed ||
                                            provider.users_who_reviewed ||
                                            [],
                                        tags: allTags,
                                    };
                                }
                                return provider;
                            });
                        });
                    }
                }
            } catch (error) {
                console.error(
                    `Failed to update recommendation ${selectedProvider.id}:`,
                    error
                );
                // Fallback: refresh all providers
                const currentRawProviders = JSON.parse(
                    JSON.stringify(rawProviders)
                );
                await processAndSetProviders(currentRawProviders);
            }
        } catch (err) {
            alert(`Error submitting review: ${err.message}`);
        }
    };

    const handleLike = async (recommendationId) => {
        if (!currentUserId || !currentUserEmail) {
            alert("Please log in to like/unlike a recommendation.");
            return;
        }

        const providerToUpdate = rawProviders.find(
            (p) => p.id === recommendationId
        );
        if (!providerToUpdate) {
            console.error(
                "Provider not found for like action:",
                recommendationId
            );
            return;
        }

        const originalRawProviders = JSON.parse(JSON.stringify(rawProviders));
        const originalLikedRecommendations = new Set(likedRecommendations);

        const newCurrentUserLikedState = !providerToUpdate.currentUserLiked;
        const newNumLikes = newCurrentUserLikedState
            ? (providerToUpdate.num_likes || 0) + 1
            : Math.max(0, (providerToUpdate.num_likes || 1) - 1);

        setRawProviders((prevProviders) =>
            prevProviders.map((provider) =>
                provider.id === recommendationId
                    ? {
                          ...provider,
                          num_likes: newNumLikes,
                          currentUserLiked: newCurrentUserLikedState,
                      }
                    : provider
            )
        );

        if (newCurrentUserLikedState) {
            setLikedRecommendations((prevLiked) =>
                new Set(prevLiked).add(recommendationId)
            );
        } else {
            setLikedRecommendations((prevLiked) => {
                const newSet = new Set(prevLiked);
                newSet.delete(recommendationId);
                return newSet;
            });
        }

        try {
            const response = await fetch(
                `${API_URL}/api/providers/${recommendationId}/like`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        userId: currentUserId,
                        userEmail: currentUserEmail,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response
                    .json()
                    .catch(() => ({
                        message: "Server error during like/unlike action.",
                    }));
                throw new Error(
                    errorData.message ||
                        `Failed to update like status. Status: ${response.status}`
                );
            }
            const result = await response.json();

            setRawProviders((prevProviders) =>
                prevProviders.map((provider) =>
                    provider.id === recommendationId
                        ? {
                              ...provider,
                              num_likes: parseInt(result.num_likes, 10) || 0,
                              currentUserLiked: result.currentUserLiked,
                          }
                        : provider
                )
            );

            if (result.currentUserLiked) {
                setLikedRecommendations((prev) =>
                    new Set(prev).add(recommendationId)
                );
            } else {
                setLikedRecommendations((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(recommendationId);
                    return newSet;
                });
            }
        } catch (error) {
            console.error("Error updating like status:", error.message);
            setRawProviders(originalRawProviders);
            setLikedRecommendations(originalLikedRecommendations);
            alert(`Failed to update like status: ${error.message}`);
        }
    };

    const handleBumpNetwork = () => {
        setShowBumpNetworkModal(true);
    };

    // Batch fetch comments for multiple recommendations
    const fetchBatchComments = async (recommendations) => {
        if (!recommendations || recommendations.length === 0) return;

        setIsLoadingComments(true);
        try {
            const serviceIds = recommendations
                .map((rec) => rec.id)
                .filter(Boolean);

            if (serviceIds.length === 0) return;

            const response = await fetch(`${API_URL}/api/comments/batch`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ service_ids: serviceIds }),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.comments) {
                    const commentsMap = new Map();
                    Object.entries(data.comments).forEach(
                        ([serviceId, comments]) => {
                            commentsMap.set(String(serviceId), comments || []);
                        }
                    );
                    setCommentsMap(commentsMap);
                }
            }
        } catch (error) {
            console.error("Error fetching batch comments:", error);
        } finally {
            setIsLoadingComments(false);
        }
    };

    // Handle new comment added
    const handleCommentAdded = (serviceId, newComment) => {
        setCommentsMap((prev) => {
            const newMap = new Map(prev);
            const existingComments = newMap.get(serviceId) || [];
            newMap.set(serviceId, [newComment, ...existingComments]);
            return newMap;
        });
    };

    // Handle provider click from map
    const handleProviderClick = (providerId) => {
        // Open provider page in a new tab
        window.open(`/provider/${providerId}`, "_blank");
    };

    // Hide the loading spinner when transitioning from search to results
    // if (isLoading && rawProviders.length === 0 && query) {
    //     return (
    //         <div className="search-results-container">
    //             <div className="loading-spinner">
    //                 {/* Loading silently - no text */}
    //             </div>
    //         </div>
    //     );
    // }

    // Only show errors when not loading and no results
    if (error && !isLoading && rawProviders.length === 0) {
        return (
            <div className="no-results-container elite-no-results">
                <FaBullhorn className="no-results-icon" />
                <h2>Error During Search for "{query}"</h2>
                <p className="no-results-subtext">{error}</p>
                <p className="no-results-subtext">
                    Please try again later or refine your search.
                </p>
            </div>
        );
    }

    // Check if we're waiting for location for a spatial query
    const isSpatialQuery = query && (query.toLowerCase().includes('near me') || 
                                    query.toLowerCase().includes('close to me') || 
                                    query.toLowerCase().includes('around me'));
    
    if (isSpatialQuery && !userLocation && isLoading) {
        return (
            <div className="search-results-container">
                <div className="loading-container">
                    <h2>📍 Getting your location...</h2>
                    <p>Please allow location access to find services near you.</p>
                    <div className="loading-spinner"></div>
                </div>
            </div>
        );
    }
    
    // Show extended loading state when search is taking a long time
    if (isSearchInProgress && rawProviders.length === 0) {
        const searchDuration = searchStartTime ? Math.floor((Date.now() - searchStartTime) / 1000) : 0;
        
        return (
            <div className="search-results-container">
                <div className="loading-container">
                    <h2>🔍 Searching for "{query}"...</h2>
                    {searchDuration > 30 && (
                        <p className="search-taking-time">
                            This search is taking longer than usual. Our system is thoroughly searching through recommendations to find the best matches for you.
                        </p>
                    )}
                    {searchDuration > 45 && (
                        <p className="search-taking-time">
                            Still searching... This might be a complex query or we're finding providers in your area. Please be patient.
                        </p>
                    )}
                    {searchDuration > 60 && (
                        <div className="search-retry-section">
                            <p className="search-taking-time">
                                Search is taking longer than expected. You can:
                            </p>
                            <div className="search-retry-options">
                                <button 
                                    className="retry-button"
                                    onClick={() => {
                                        // Reset search state and retry
                                        setSearchStartTime(null);
                                        setSearchTimeout(null);
                                        initialProvidersRef.current = null;
                                        processedInitialProvidersRef.current = false;
                                        setError(null);
                                        // Trigger a new search by updating dependencies
                                        window.location.reload();
                                    }}
                                >
                                    🔄 Retry Search
                                </button>
                                <p className="retry-note">
                                    Or wait a bit longer - the search may still complete successfully.
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="loading-spinner"></div>
                    <p className="search-timer">Searching for {searchDuration}s...</p>
                </div>
            </div>
        );
    }

    if (
        (noResultsParam || filteredProviders.length === 0) &&
        query &&
        !isLoading &&
        !isSearchInProgress // Only show Bump Your Network if search is truly complete
    ) {
        return (
            <BumpYourNetwork query={query} currentUser={user} isPage={true} />
        );
    }

    if (!query && rawProviders.length === 0 && !isLoading) {
        return (
            <div className="search-results-container">
                <h1 className="section-heading">
                    Search for Trusted Recommendations
                </h1>
                <p
                    style={{
                        textAlign: "center",
                        fontSize: "1.1rem",
                        color: "#4a5568",
                    }}
                >
                    Enter a search term above to find providers recommended by
                    your network.
                </p>
            </div>
        );
    }

    return (
        <div className="search-results-container">
            {/* Perplexity-style Header */}
            {query && (
                <div className="perplexity-header">
                    <h1 className="question-title">{query}</h1>
                    <div className="answer-map-tabs">
                        <button 
                            className={`answer-map-tab ${viewMode === "list" ? "active" : ""}`}
                            onClick={() => setViewMode("list")}
                        >
                            <svg className="answer-map-tab-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            AI Overview
                        </button>
                        <button 
                            className={`answer-map-tab ${viewMode === "map" ? "active" : ""}`}
                            onClick={() => {
                                setViewMode("map");
                                // Scroll to the map section
                                setTimeout(() => {
                                    const mapElement = document.getElementById('map-view');
                                    if (mapElement) {
                                        const rect = mapElement.getBoundingClientRect();
                                        const scrollTop = window.pageYOffset + rect.top - 100; // 100px offset
                                        window.scrollTo({
                                            top: scrollTop,
                                            behavior: 'smooth'
                                        });
                                    }
                                }, 100); // Small delay to ensure map is rendered
                            }}
                        >
                            <svg className="answer-map-tab-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M21 10C21 17 12 23 12 23S3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Map
                        </button>
                        <button 
                            className={`answer-map-tab ${viewMode === "list" ? "active" : ""}`}
                            onClick={() => {
                                // Switch to list view to show detailed recommendations
                                setViewMode("list");
                                
                                // Scroll to just before the detailed recommendations section
                                const detailedRecsSection = document.getElementById('detailed-recommendations');
                                if (detailedRecsSection) {
                                    // Get the element's position and scroll to just before it
                                    const rect = detailedRecsSection.getBoundingClientRect();
                                    const scrollTop = window.pageYOffset + rect.top - 100; // 100px offset to stop before the header
                                    window.scrollTo({
                                        top: scrollTop,
                                        behavior: 'smooth'
                                    });
                                }
                            }}
                        >
                            <svg className="answer-map-tab-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Detailed Recs
                        </button>
                    </div>
                    <div className="question-underline"></div>
                </div>
            )}
            
            {/* AI Summary - Primary Answer */}
            {query && filteredProviders.length > 0 && (
                <div className="answer-content">
                    <AISummary
                        query={query}
                        recommendations={filteredProviders}
                        isVisible={true}
                        showAddToList={false}
                    />
                </div>
            )}
            
            {/* Show message when no results found */}
            {query && filteredProviders.length === 0 && !isLoading && (
                <div className="answer-content">
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        No recommendations found for "{query}". Try adjusting your search terms or location.
                    </div>
                </div>
            )}

            {/* Visual Separator */}
            {query && filteredProviders.length > 0 && (
                <div className="section-separator" id="detailed-recommendations">
                    <div className="separator-line"></div>
                    <div className="separator-text">Detailed Recommendations</div>
                    <div className="separator-line"></div>
                </div>
            )}

            {availableCities.length > 0 && (
                <div className="profile-city-filter-toggle-section">
                    <button
                        className="profile-city-filter-toggle"
                        onClick={() => setShowCityFilter(!showCityFilter)}
                    >
                        <FaMapMarkerAlt className="profile-filter-icon" />
                        <span className="filter-button-text">
                            <span className="filter-button-text-long">
                                Filter by{" "}
                            </span>
                            <span>City</span>
                        </span>
                        {selectedCities.length > 0 && (
                            <span className="profile-active-filters-badge">
                                {selectedCities.length}
                            </span>
                        )}
                        <FaChevronDown
                            className={`profile-filter-chevron ${
                                showCityFilter ? "rotated" : ""
                            }`}
                        />
                    </button>
                    {showCityFilter && (
                        <div className="profile-city-filter-wrapper">
                            <div className="profile-city-filter-checkboxes">
                                {availableCities.map(([cityName, count]) => (
                                    <div
                                        key={cityName}
                                        className="profile-city-checkbox-item"
                                    >
                                        <input
                                            type="checkbox"
                                            id={`city-${cityName.replace(
                                                /\s+/g,
                                                "-"
                                            )}`}
                                            name={cityName}
                                            checked={selectedCities.includes(
                                                cityName
                                            )}
                                            onChange={() =>
                                                handleCitySelection(cityName)
                                            }
                                        />
                                        <label
                                            htmlFor={`city-${cityName.replace(
                                                /\s+/g,
                                                "-"
                                            )}`}
                                            className="profile-city-checkbox-label"
                                        >
                                            {cityName}
                                        </label>
                                        <span className="profile-city-count">
                                            ({count})
                                        </span>
                                    </div>
                                ))}
                                {selectedCities.length > 0 && (
                                    <button
                                        onClick={() => setSelectedCities([])}
                                        className="profile-city-clear-all"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Loading spinner hidden for cleaner UI */}
            {/* {isLoading && query && (
                <div className="loading-spinner" style={{ paddingTop: "1rem" }}>
                    Updating results...
                </div>
            )} */}

            {!isLoading && filteredProviders.length > 0 && (
                <>
                    {/* Content based on selected tab */}
                    {viewMode === "list" && (
                        <div className="recommendations-feed">
                            {filteredProviders.map((p) => (
                                <RecommendationCard
                                    key={p.id}
                                    rec={{
                                        ...p,
                                        provider_id: p.id
                                    }}
                                    onWriteReview={(provider) => {
                                        // Create URL parameters for prefilling the recommendation form
                                        const params = new URLSearchParams();
                                        
                                        // Add the provider ID to link to existing service provider
                                        if (provider.id) {
                                            params.set('providerId', provider.id);
                                        }
                                        
                                        if (provider.business_name || provider.name) {
                                            params.set('businessName', provider.business_name || provider.name);
                                        }
                                        if (provider.contact_name) {
                                            params.set('contactName', provider.contact_name);
                                        }
                                        if (provider.website) {
                                            params.set('website', provider.website);
                                        }
                                        if (provider.phone_number) {
                                            params.set('phoneNumber', provider.phone_number);
                                        }
                                        if (provider.street_address) {
                                            params.set('streetAddress', provider.street_address);
                                        }
                                        if (provider.city) {
                                            params.set('city', provider.city);
                                        }
                                        if (provider.state) {
                                            params.set('state', provider.state);
                                        }
                                        if (provider.zip_code) {
                                            params.set('zipCode', provider.zip_code);
                                        }
                                        if (provider.google_place_id) {
                                            params.set('googlePlaceId', provider.google_place_id);
                                        }
                                        if (provider.latitude && provider.longitude) {
                                            params.set('latitude', provider.latitude);
                                            params.set('longitude', provider.longitude);
                                        }
                                        if (provider.tags && Array.isArray(provider.tags) && provider.tags.length > 0) {
                                            params.set('tags', provider.tags.join(', '));
                                        }
                                        
                                        // Navigate to share-recommendation page with prefilled data
                                        navigate(`/share-recommendation?${params.toString()}`);
                                    }}
                                    onLike={handleLike}
                                    isLikedByCurrentUser={likedRecommendations.has(
                                        p.id
                                    )}
                                    loggedInUserId={currentUserId}
                                    currentUserName={user?.firstName}
                                    comments={
                                        commentsMap.get(String(p.id)) || []
                                    }
                                    onCommentAdded={handleCommentAdded}
                                />
                            ))}
                        </div>
                    )}

                    {/* Map View */}
                    {viewMode === "map" && (
                        <div className="map-view-container" id="map-view">
                            <MapView
                                providers={filteredProviders.filter(
                                    (p) => p.latitude && p.longitude
                                )}
                                userLocation={userLocation}
                                onProviderClick={handleProviderClick}
                            />
                        </div>
                    )}
                </>
            )}

            {isReviewModalOpen && selectedProvider && (
                <ReviewModal
                    isOpen={isReviewModalOpen}
                    onClose={() => setIsReviewModalOpen(false)}
                    onSubmit={handleReviewSubmit}
                    provider={selectedProvider}
                />
            )}
            {isQuoteModalOpen && selectedProvider && (
                <QuoteModal
                    isOpen={isQuoteModalOpen}
                    providerName={selectedProvider.business_name}
                    providerEmail={selectedProvider.email}
                    providerPhotoUrl={selectedProvider.profile_image}
                    onClose={() => setIsQuoteModalOpen(false)}
                />
            )}
            <SuccessModal
                isOpen={isSuccessModalOpen}
                onClose={() => setIsSuccessModalOpen(false)}
                message={successMessage}
                title="Review Submitted!"
            />
            {clickedRecommender && (
                <div className="modal-overlay">
                    <div className="simple-modal">
                        <button
                            className="modal-close-x"
                            onClick={() => setClickedRecommender(null)}
                        >
                            ×
                        </button>
                        <h3>
                            Want to connect with{" "}
                            <span className="highlight">
                                {clickedRecommender}
                            </span>
                            ?
                        </h3>
                        <div className="modal-buttons-vertical">
                            <button
                                className="secondary-button"
                                onClick={() => {
                                    setClickedRecommender(null);
                                    setShowFeatureComingModal(true);
                                }}
                            >
                                Thank {clickedRecommender}
                            </button>
                            <button
                                className="secondary-button"
                                onClick={() => {
                                    setClickedRecommender(null);
                                    setShowFeatureComingModal(true);
                                }}
                            >
                                Ask {clickedRecommender} a question
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showFeatureComingModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button
                            className="modal-close-x"
                            onClick={() => setShowFeatureComingModal(false)}
                        >
                            ×
                        </button>
                        <p>
                            We're about to launch this feature. Stay tuned{" "}
                            <FaEye style={{ marginLeft: "5px" }} />
                        </p>
                        <div className="modal-buttons">
                            <button
                                className="primary-button"
                                onClick={() => setShowFeatureComingModal(false)}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <BumpYourNetwork
                isOpen={showBumpNetworkModal}
                onClose={() => setShowBumpNetworkModal(false)}
                query={query}
                currentUser={user}
            />
        </div>
    );
};

export default Search;
